/**
 * Date Formatting Utilities
 * All date formatting MUST use Intl.DateTimeFormat for proper timezone handling
 * Never use toLocaleDateString, toLocaleTimeString, or toDateString directly
 */

const DEFAULT_LOCALE = 'en-US';

/**
 * Format month with year (e.g., "February 2026")
 */
export const formatMonthYear = (date: Date): string => {
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    month: 'long',
    year: 'numeric',
  }).format(date);
};

/**
 * Format short date (e.g., "Feb 3")
 */
export const formatShortDate = (date: Date): string => {
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

/**
 * Format full date (e.g., "Feb 3, 2026")
 */
export const formatFullDate = (date: Date): string => {
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

/**
 * Format long date (e.g., "February 3, 2026")
 */
export const formatLongDate = (date: Date): string => {
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

/**
 * Format date with weekday and time (e.g., "Mon, Feb 3, 10:30 PM")
 */
export const formatDateWithWeekdayAndTime = (date: Date): string => {
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Format time only in 24h format (e.g., "15:30")
 */
export const formatTime24h = (date: Date, timeZone?: string): string => {
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timeZone ?? undefined,
  }).format(date);
};

/**
 * Format time only in 12h format (e.g., "3:30 PM")
 */
export const formatTime12h = (date: Date): string => {
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

/**
 * Format weekday only (e.g., "Mon")
 */
export const formatWeekday = (date: Date): string => {
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    weekday: 'short',
  }).format(date);
};

/**
 * Check if two dates are the same day (using Intl for consistency)
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  const formatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date1) === formatter.format(date2);
};

/**
 * Get date-only string for comparison (YYYY-MM-DD format)
 */
export const getDateKey = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Normalize a date value to ISO string. Use for API/JSON consistency (e.g. challenge.createdAt, match.date).
 */
export function toISOStringSafe(value: Date | string | undefined | null): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  return '';
}

/**
 * Format game date only for match header (e.g. "Wed 5 Feb") when time is unknown.
 * @param dateStr - YYYY-MM-DD string
 */
export const formatGameDateOnly = (dateStr: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (y == null || m == null || d == null) return '';
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return '';
  return `${formatWeekday(date)} ${formatShortDate(date)}`;
};
